
import { EventBus } from '@/shared/app/connector/EventBus';
import Template from '@/shared/app/model/templating/Template';
import Base from '@/shared/app/ui/Base.vue';
import _ from 'lodash';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { ALTERNATE_TRIGGERS } from './alternate.constants';

@Component
export default class Alternate extends Base {
  activeIndex = 0;

  private nextTimeout = 0;

  private waitingForDone = false;

  private backdoor = true;

  private events: { event: string; callback: Function }[] = [];

  beforeDestroy() {
    clearTimeout(this.nextTimeout);
    this.events.forEach(({ event, callback }) => {
      EventBus.$off(event, callback);
    });
  }

  mounted() {
    // Amorçage
    this.setSlotNextFn();
  }

  next() {
    // Si on est à la fin de la boucle
    if (this.activeIndex >= this.lastIndex) {
      // Ce composant Alternate a terminé sa boucle
      this.callDone();
      // On reprend au début
      this.activeIndex = 0;
    } else {
      // Suivant
      this.activeIndex += 1;
    }
    // Application du changement
    this.setSlotNextFn();
  }

  setSlotNextFn() {
    const slotName = this.activeSlotName;

    let trigger = this.config[`${slotName}Trigger`];
    // Si le slot n'a pas de trigger défini, on utilise l'éventuel trigger général
    if (!trigger) trigger = this.config.trigger;
    // Si le trigger général n'est pas renseigné, alors c'est DURATION (par défaut)
    if (!trigger) trigger = ALTERNATE_TRIGGERS.TRIGGER_DURATION;

    // Trigger : DONE
    // Alternate va attendre la remontée d'un événement DONE du slot
    this.waitingForDone = false;
    if (trigger === ALTERNATE_TRIGGERS.TRIGGER_DONE) {
      this.waitingForDone = true;
      return;
    }

    // Trigger : EVENT
    // Alternate va attendre qu'un événement soit fired au niveau de "l'eventbus global"
    if (trigger === ALTERNATE_TRIGGERS.TRIGGER_EVENT) {
      // Événement spécifique au slot ?
      let event = this.config[`${slotName}Event`]?.toString();
      // Événement général ?
      if (!event) event = this.config.event?.toString();
      if (event) {
        // Uniquement si l'évènement est valide
        const cb = () => {
          this.next();
        };
        this.events.push({ event, callback: cb });
        EventBus.$once(event, cb);
        return;
      }
      // Si l'événement n'est pas valide
      // le fallback va rattraper le coup (ci-dessous)
    }

    // Fallback : par durée
    let duration = this.config[`${slotName}Duration`]?.toString();
    if (!duration) duration = this.config.duration?.toString();
    // Il y a une durée par défaut
    clearTimeout(this.nextTimeout);
    this.nextTimeout = setTimeout(() => {
      this.next();
    }, Number.parseInt(duration, 10) || 8000);
  }

  /**
   * Requis pour détecter correctement les changements de configuration
   * des slots dans le template du composant Alternante
   */
  @Watch('pTemplate.slots')
  slotsChange() {
    this.backdoor = !this.backdoor;
  }

  get activeSlotName() {
    return Object.keys(this.filteredScopedSlots)[this.activeIndex];
  }

  get count() {
    return Object.keys(this.filteredScopedSlots).length;
  }

  get lastIndex() {
    return Math.max(0, this.count - 1);
  }

  callDone(fromTemplate?: Template) {
    // find
    if (this.pTemplate?.slots) {
      const fromSlotName = _.map(
        this.pTemplate.slots,
        (template, slotName) => ({
          template,
          slotName,
        }),
      ).find(({ template }) => {
        return template === fromTemplate;
      })?.slotName;
      if (this.waitingForDone && this.activeSlotName === fromSlotName) {
        this.next();
      }
    }
  }

  doneCallback(slotName: string) {
    return () => {
      if (this.waitingForDone && this.activeSlotName === slotName) {
        this.next();
      }
    };
  }

  get filteredScopedSlots() {
    // eslint-disable-next-line no-unused-expressions
    this.backdoor;
    const r: typeof Vue.prototype.$scopedSlots = {};

    /*
     Via le "activator", les slots peuvent être affichés ou désactivés
     Ici, on filtre les slots éventuellement désactivés
    */
    Object.entries(this.$scopedSlots).forEach(([slotName, slot]) => {
      if (this.config[`${slotName}Activator`]) {
        const [datasetName, datasetKey] = this.config[`${slotName}Activator`]
          .toString()
          .split('.');
        if (
          !datasetName ||
          !datasetKey ||
          this.dataset(datasetName)[datasetKey] !== true
        )
          return;
      }
      // Le slot est actif ou ne comporte pas de "activator"
      // À afficher.
      r[slotName] = slot;
    });
    return r;
  }

  /**
   * Renvoie le nom de la transition utilisée (bind une classe CSS)
   */
  get transition(): string {
    return String(this.config.transition) || 'none';
  }
}
